import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Shield, TrendingUp, TrendingDown, Check, FileSignature } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientProductDetail() {
  const { productId } = useParams();
  const { token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [step, setStep] = useState(1);
  const [consents, setConsents] = useState({
    terms: false,
    risks: false,
    data: false
  });
  const [signatureData, setSignatureData] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [productId, token]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      navigate('/client/products');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!amount || parseFloat(amount) < product.min_investment) {
      toast.error(t('error'), { description: `Montant minimum: €${product.min_investment}` });
      return;
    }
    setStep(2);
  };

  const handleSign = async () => {
    if (!consents.terms || !consents.risks || !consents.data) {
      toast.error(t('error'), { description: 'Veuillez accepter tous les consentements' });
      return;
    }
    if (!signatureData) {
      toast.error(t('error'), { description: 'Veuillez signer le contrat' });
      return;
    }

    setSubmitting(true);
    try {
      // Create contract
      const contractRes = await axios.post(`${API}/contracts`, {
        product_id: productId,
        amount: parseFloat(amount),
        currency: 'EUR'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Sign contract
      await axios.post(`${API}/contracts/${contractRes.data.contract_id}/sign`, {
        signature_data: signatureData,
        consents: Object.keys(consents).filter(k => consents[k])
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('success'), { description: 'Contrat signé avec succès' });
      setSubscribeDialogOpen(false);
      navigate('/client/contracts');
    } catch (error) {
      toast.error(t('error'), { description: error.response?.data?.detail || 'Erreur lors de la souscription' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  if (!product) return null;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/client/products')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
              {product.name}
            </h2>
            <p className="text-sm text-muted-foreground capitalize">
              {product.category?.replace(/_/g, ' ')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="card-premium">
              <CardHeader>
                <CardTitle className="text-lg">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{product.description}</p>
              </CardContent>
            </Card>

            {/* Scenarios */}
            <Card className="card-premium">
              <CardHeader>
                <CardTitle className="text-lg">Scénarios de rendement</CardTitle>
                <CardDescription>Projections indicatives basées sur les conditions de marché</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-sm bg-emerald-500/10 border border-emerald-500/20">
                    <TrendingUp className="w-5 h-5 text-emerald-500 mb-2" />
                    <p className="text-xs text-muted-foreground">Favorable</p>
                    <p className="text-xl font-bold text-emerald-500">+{product.scenario_favorable}%</p>
                  </div>
                  <div className="p-4 rounded-sm bg-[#D4AF37]/10 border border-[#D4AF37]/20">
                    <Shield className="w-5 h-5 text-[#D4AF37] mb-2" />
                    <p className="text-xs text-muted-foreground">Neutre</p>
                    <p className="text-xl font-bold text-[#D4AF37]">+{product.scenario_neutral}%</p>
                  </div>
                  <div className="p-4 rounded-sm bg-red-500/10 border border-red-500/20">
                    <TrendingDown className="w-5 h-5 text-red-500 mb-2" />
                    <p className="text-xs text-muted-foreground">Défavorable</p>
                    <p className={`text-xl font-bold ${product.scenario_unfavorable >= 0 ? 'text-red-500' : 'text-red-500'}`}>
                      {product.scenario_unfavorable >= 0 ? '+' : ''}{product.scenario_unfavorable}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="card-premium">
              <CardContent className="p-6 space-y-4">
                <div className="text-center p-4 rounded-sm bg-[#D4AF37]/10">
                  <p className="text-sm text-muted-foreground">Rendement annuel</p>
                  <p className="text-4xl font-bold text-[#D4AF37]">{product.return_rate}%</p>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('min_investment')}</span>
                    <span className="font-semibold">€{product.min_investment?.toLocaleString()}</span>
                  </div>
                  {product.max_investment && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('max_investment')}</span>
                      <span className="font-semibold">€{product.max_investment?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Fréquence</span>
                    <span className="font-semibold capitalize">{product.frequency}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Capital garanti</span>
                    <Badge className={product.capital_guaranteed ? 'status-approved' : 'status-pending'}>
                      {product.capital_guaranteed ? 'Oui' : 'Non'}
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('risk_level')}</span>
                    <Badge className={
                      product.risk_level === 'low' ? 'bg-emerald-500/20 text-emerald-500' :
                      product.risk_level === 'medium' ? 'bg-yellow-500/20 text-yellow-500' :
                      'bg-red-500/20 text-red-500'
                    }>
                      {t(product.risk_level)}
                    </Badge>
                  </div>
                </div>

                <Dialog open={subscribeDialogOpen} onOpenChange={setSubscribeDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full btn-gold" data-testid="subscribe-btn">
                      <FileSignature className="w-4 h-4 mr-2" />
                      {t('subscribe')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="glass max-w-md">
                    <DialogHeader>
                      <DialogTitle>
                        {step === 1 ? 'Souscription' : 'Signature électronique'}
                      </DialogTitle>
                    </DialogHeader>

                    {step === 1 ? (
                      <div className="space-y-6 mt-4">
                        <div className="space-y-2">
                          <Label>Montant à investir (€)</Label>
                          <Input
                            type="text"
                            inputMode="numeric"
                            value={amount}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9]/g, '').replace(/^0+/, '') || '';
                              setAmount(val);
                            }}
                            onFocus={(e) => e.target.select()}
                            placeholder={`Min: €${product.min_investment}`}
                            className="bg-card/50"
                            data-testid="subscription-amount"
                          />
                        </div>

                        {amount && parseFloat(amount) >= product.min_investment && (
                          <div className="p-4 rounded-sm bg-white/5">
                            <p className="text-sm text-muted-foreground mb-2">Simulation</p>
                            <div className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span>Capital investi</span>
                                <span className="font-mono">€{parseFloat(amount).toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span>Rendement annuel estimé</span>
                                <span className="font-mono text-emerald-500">
                                  +€{((parseFloat(amount) * product.return_rate) / 100).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        <Button 
                          onClick={handleSubscribe} 
                          className="w-full btn-gold"
                          disabled={!amount || parseFloat(amount) < product.min_investment}
                          data-testid="continue-to-sign"
                        >
                          Continuer vers la signature
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-6 mt-4">
                        {/* Consents */}
                        <div className="space-y-3">
                          <p className="text-sm font-medium">Consentements obligatoires</p>
                          
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={consents.terms}
                              onChange={(e) => setConsents(prev => ({ ...prev, terms: e.target.checked }))}
                              className="mt-1"
                            />
                            <span className="text-sm text-muted-foreground">
                              J'accepte les conditions générales du contrat
                            </span>
                          </label>

                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={consents.risks}
                              onChange={(e) => setConsents(prev => ({ ...prev, risks: e.target.checked }))}
                              className="mt-1"
                            />
                            <span className="text-sm text-muted-foreground">
                              J'ai pris connaissance des risques liés à ce placement
                            </span>
                          </label>

                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={consents.data}
                              onChange={(e) => setConsents(prev => ({ ...prev, data: e.target.checked }))}
                              className="mt-1"
                            />
                            <span className="text-sm text-muted-foreground">
                              J'autorise le traitement de mes données personnelles
                            </span>
                          </label>
                        </div>

                        {/* Signature */}
                        <div className="space-y-2">
                          <Label>Signature (tapez votre nom complet)</Label>
                          <Input
                            value={signatureData}
                            onChange={(e) => setSignatureData(e.target.value)}
                            placeholder="Prénom NOM"
                            className="bg-card/50 font-mono"
                            data-testid="signature-input"
                          />
                        </div>

                        <div className="flex gap-3">
                          <Button 
                            variant="outline" 
                            onClick={() => setStep(1)}
                            className="flex-1"
                          >
                            Retour
                          </Button>
                          <Button 
                            onClick={handleSign}
                            disabled={submitting || !consents.terms || !consents.risks || !consents.data || !signatureData}
                            className="flex-1 btn-gold"
                            data-testid="sign-contract-btn"
                          >
                            {submitting ? t('loading') : 'Signer le contrat'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
